/**
 * This cl-results-table renders the following configs on the Cost of Living Calculator:
 * - client/config/col-calculator/results-table - 6 items
 */

// Dependencies
import React, { useState } from 'react';

import classnames from 'classnames';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import { map, get, isFunction } from 'lodash';
import { Box, Text } from '@nerdwallet/currency';
import { ControlledAccordion } from '@nerdwallet/react-accordion';
import num from '@nerdwallet/nw-client-lib/num';
import { colors } from '@nerdwallet/base-styles/variables';
import CityResultsTable from '../../shared/city-results-table/city-results-table';
import { ViewConfigProps, DataConfigProps } from '../../shared/types';
import styles from './col-results-table.module.less';
import { debouncedTrackInputEvent } from '../../../../lib/analytics/trackers';
import useAnalytics from '../../../../lib/analytics/use-analytics';

type ColResultsTableProps = {
  trackingFormName?: string;
  data: DataConfigProps;
  viewConfig: ViewConfigProps[];
};

const SEGMENT_ANALYTICS_FIELD_MAP = {
  housing_open: 'Housing Costs',
  transportation_open: 'Transportation Costs',
  food_open: 'Food Costs',
  entertainment_open: 'Entertainment Costs',
  healthcare_open: 'Healthcare Costs',
};

const ColResultsTable = ({
  data,
  trackingFormName = null,
  viewConfig,
}: ColResultsTableProps): JSX.Element => {
  const segmentAbstract = useAnalytics();
  const [activeIndex, setActiveIndex] = useState(0);

  const toggleItem = (index) => {
    const isExpandingSection = activeIndex !== index;
    // segment analytics
    debouncedTrackInputEvent(
      {
        entityName: 'form_field_section',
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        field: SEGMENT_ANALYTICS_FIELD_MAP[accordionProps.items[index].id],
        field_type: 'expand-contract',
        field_value: isExpandingSection ? 'expand' : 'contract',
      },
      segmentAbstract
    );

    if (isExpandingSection) {
      setActiveIndex(index);
    } else {
      setActiveIndex(null);
    }
  };

  const getPercentTextColorAndHeaderValue = (header) => {
    let evaluatedHeader = num.formatPercent(header, '0%');
    let percentTextColor = '';

    if (header.charAt(0) === '-') {
      evaluatedHeader = `${evaluatedHeader.substring(1)} lower`;
      percentTextColor = colors.green;
    } else {
      evaluatedHeader = `${evaluatedHeader} higher`;
      percentTextColor = colors['red-dark'];
    }

    return [percentTextColor, evaluatedHeader];
  };

  const accordionProps = {
    analytics: {
      enabled: true,
      payload: {
        entityName: trackingFormName,
      },
    },
    items: map(
      viewConfig,
      (
        {
          header,
          trackingFieldName,
          headerValue,
          className,
          contentConfig,
          isVisible,
        },
        index
      ) => {
        const keyVal = `col-results-table-row-${index}`;

        if (isFunction(isVisible) && isVisible(data) === false) {
          return {
            id: trackingFieldName,
            expanded: activeIndex === index,
            onSelect: () => toggleItem(index),
            title: null,
            children: null,
            key: keyVal,
          };
        }

        const getHeader = get(data, headerValue);
        const [percentTextColor, evaluatedHeaderValue] = getHeader
          ? getPercentTextColorAndHeaderValue(getHeader?.toString())
          : ['', ''];

        return {
          id: trackingFieldName,
          expanded: activeIndex === index,
          onSelect: () => toggleItem(index),
          key: keyVal,
          title: (
            <Box>
              <Box className={classnames(styles.toolTip, styles[className])} />
              <Box display="flex" justifyContent="space-between">
                <Text bold={false}>{header}</Text>
                <Box marginRight={3} style={{ color: percentTextColor }}>
                  <Text bold={activeIndex === index}>
                    {`${evaluatedHeaderValue}`}
                  </Text>
                </Box>
              </Box>
            </Box>
          ),
          children: <CityResultsTable config={contentConfig} data={data} />,
        };
      }
    ),
  };

  return (
    <Box
      paddingY={{ desktop: 3, mobile: 0 }}
      paddingX={{ desktop: 1, mobile: 0 }}
      paddingTop={{ mobile: 3 }}
      style={{ color: colors['neutral-dark'] }}
    >
      <ControlledAccordion {...accordionProps} />
    </Box>
  );
};

ColResultsTable.displayName = 'ColResultsTable';

export default ColResultsTable;
